/**
 * ARIA del despacho — panel de chat a nivel cartera (cross-empresa, read-only).
 *
 * A diferencia del AIPanel de empresa (que requiere una empresa activa y
 * llama a `/api/functions/invoke`), este panel vive en el ecosistema de
 * despacho (Portal del Asesor, SIN empresa activa) y habla con el carril
 * paralelo `POST /api/asesor/aria/chat`. Mantiene su PROPIO estado de
 * conversación local (no el store global por-empresa `useAriaConversation`,
 * que colisionaría) y, cuando ARIA propone "entrar en una empresa"
 * (`navigation.company_id`), dispara `switchCompany` para llevar al asesor a
 * esa empresa en modo dios.
 */

import { useState, useRef, useEffect } from 'react';
import { Bot, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import { apiFetch } from '@/utils/api';
import { toastApiError } from '@/lib/query-client';
import { useApp } from '@/components/hooks/useApp';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import AIComposer from '@/components/ai/v2/AIComposer';

const SUGGESTIONS = [
  'Resume el estado de mi cartera',
  '¿Qué empresas tienen alertas críticas?',
  'Modelos AEAT pendientes este mes',
  'Honorarios vencidos por cobrar',
];

function MessageBubble({ role, content, error }) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-body-sm leading-relaxed',
          isUser
            ? 'rounded-br-sm bg-primary text-primary-foreground'
            : error
              ? 'rounded-bl-sm bg-destructive/10 text-destructive'
              : 'rounded-bl-sm bg-muted text-foreground',
        )}
      >
        {content}
      </div>
    </div>
  );
}

export function AsesorAriaChat({ open, onOpenChange }) {
  const { switchCompany } = useApp();
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const scrollRef = useRef(null);
  // La respuesta puede llegar con el Sheet ya cerrado: `onSuccess` lo lee del
  // ref, no del closure de la petición.
  const openRef = useRef(open);
  openRef.current = open;

  const chat = useMutation({
    mutationFn: (text) =>
      apiFetch('/api/asesor/aria/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: text,
          conversationId: conversationId ?? undefined,
          // El backend cap a 30; enviamos los últimos turnos como contexto.
          conversationHistory: messages
            .slice(-30)
            .map((m) => ({ role: m.role, content: m.content })),
        }),
      }),
    onSuccess: (data) => {
      if (data?.conversationId) setConversationId(data.conversationId);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data?.response ?? 'Sin respuesta.' },
      ]);
      // ARIA quiere entrar en una empresa concreta → cambiamos de empresa
      // (modo dios). El Layout detecta el cambio y sale del shell de despacho.
      // Si el usuario ya cerró el panel, no le movemos de empresa a sus espaldas:
      // se le ofrece la entrada y decide él.
      if (data?.navigation?.company_id) {
        const enter = () => switchCompany(data.navigation.company_id, { hardReset: true });
        if (openRef.current) {
          toast.success(`Entrando en ${data.navigation.company_name}…`);
          onOpenChange(false);
          enter();
        } else {
          toast(`ARIA propone entrar en ${data.navigation.company_name}`, {
            action: { label: 'Entrar', onClick: enter },
          });
        }
      }
    },
    onError: (err) => {
      toastApiError(err, 'No se pudo consultar a ARIA del despacho');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Lo siento, ha ocurrido un error al procesar tu consulta.', error: true },
      ]);
    },
  });

  const handleSend = (text) => {
    const value = text.trim();
    if (!value || chat.isPending) return;
    setMessages((prev) => [...prev, { role: 'user', content: value }]);
    chat.mutate(value);
  };

  // Autoscroll al último mensaje.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, chat.isPending]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-[420px] flex-col gap-0 p-0 sm:max-w-[420px]">
        <SheetHeader className="border-b border-border px-4 py-3">
          <SheetTitle className="flex items-center gap-2 text-body">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Bot className="h-4 w-4" />
            </span>
            ARIA · Despacho
          </SheetTitle>
        </SheetHeader>

        <div
          ref={scrollRef}
          role="log"
          aria-live="polite"
          aria-label="Historial de conversación"
          className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
        >
          {messages.length === 0 && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-xl bg-muted/60 px-3.5 py-3 text-body-sm text-muted-foreground">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Soy ARIA a nivel despacho. Puedo resumir tu cartera, comparar empresas, priorizar alertas
                  y, cuando quieras operar en una empresa, llevarte a ella.
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSend(s)}
                    className="rounded-lg border border-border bg-card px-3 py-2 text-left text-caption text-foreground transition hover:bg-muted"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} error={m.error} />
          ))}

          {chat.isPending && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-sm bg-muted px-3.5 py-2 text-body-sm text-muted-foreground">
                Pensando…
              </div>
            </div>
          )}
        </div>

        <AIComposer onSend={handleSend} disabled={chat.isPending} placeholder="Pregunta sobre tu cartera…" />
      </SheetContent>
    </Sheet>
  );
}

export default AsesorAriaChat;
